// src/components/Dashboard.jsx
import React, { useEffect, useState, useMemo, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import CalendarWidget from "../../CalendarWidget";
import dayjs from "dayjs";
import LoadingErrorComponent from "../../LoadingError/LoadingError";
import { Doughnut } from "react-chartjs-2";
import user from "../../../assets/image/user.jpg";
import logo from "../../../assets/image/kkgt-header-image.jpg";

import {
  FaCalendarCheck,
  FaClock,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaChartPie,
  FaChartBar,
  FaCalendarAlt,
} from "react-icons/fa";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const ReceptionistDashboard = () => {
  const {
    appointments,
    appointmentsLoading,
    appointmentsError,
    getUpcomingAppointments,
    getAllPateintData,
    allPatients,
    getOPDData,
    getDoctorDetails,
    doctorDetails,
    loginUser,
  } = useContext(AuthContext);

  const today = new Date();
  const [childDate, setChildDate] = useState(today);
  const [dataRefreshTime, setDataRefreshTime] = useState(new Date());

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!appointments || appointments.length === 0) {
          await getUpcomingAppointments();
        }
        if (!allPatients || allPatients.length === 0) {
          await getAllPateintData();
        }
        await getOPDData();
        if (loginUser?.id && !doctorDetails) {
          await getDoctorDetails(loginUser.id);
        }
        setDataRefreshTime(new Date());
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleDataFromChild = (data) => {
    setChildDate(data);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A";
    return dayjs(isoDate).format("MMMM D, YYYY");
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }
    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  };

  // Memoized appointment calculations
  const todayAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = new Date(appointment.date);
        appointmentDate.setHours(0, 0, 0, 0);
        return appointmentDate.getTime() === today.getTime();
      })
      .sort((a, b) => {
        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments]);

  const selectedDateAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = new Date(appointment.date);
        const selectedDate = new Date(childDate);

        return (
          appointmentDate.getFullYear() === selectedDate.getFullYear() &&
          appointmentDate.getMonth() === selectedDate.getMonth() &&
          appointmentDate.getDate() === selectedDate.getDate()
        );
      })
      .sort((a, b) => {
        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments, childDate]);

  const parseDate = (isoDate) => {
    if (!isoDate) return new Date();
    const date = new Date(isoDate);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const upcomingAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    const today = parseDate(new Date());

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = parseDate(appointment.date);
        return appointmentDate > today;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);

        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }

        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments]);

  // Appointment status calculations
  const pendingAppointmentsCount = useMemo(() => {
    if (!appointments || appointments.length === 0) return 0;
    return appointments.filter(
      (appointment) => appointment.status?.toLowerCase() === "pending"
    ).length;
  }, [appointments]);

  const completedAppointmentsCount = useMemo(() => {
    if (!appointments || appointments.length === 0) return 0;
    return appointments.filter(
      (appointment) => appointment.status?.toLowerCase().trim() === "completed"
    ).length;
  }, [appointments]);

  // Statistics calculations
  const totalAppointments = appointments?.length || 0;
  const totalCompletedAppointments = completedAppointmentsCount;
  const todaysTotalAppointments = todayAppointments.length;
  const upcomingTotalAppointments = upcomingAppointments.length;
  const totalPendingAppointments = pendingAppointmentsCount;

  // Chart data
  const pieData = {
    labels: [
      "Total Appointments",
      "Completed",
      "Today's",
      "Upcoming",
      "Pending",
    ],
    datasets: [
      {
        data: [
          totalAppointments,
          totalCompletedAppointments,
          todaysTotalAppointments,
          upcomingTotalAppointments,
          totalPendingAppointments,
        ],
        backgroundColor: [
          "#10B981",
          "#3B82F6",
          "#8B5CF6",
          "#F59E0B",
          "#EF4444",
        ],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50">
       {/* Header */}
      <div className="bg-white shadow-sm border-b mt-10">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-emerald-500 to-teal-600 px-3 sm:px-4 py-2 rounded-lg">
            <img
              src={user}
              alt="Clinic"
              className="h-6 w-6 sm:h-8 sm:w-8 rounded-full flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h1 className="text-white font-bold text-sm sm:text-lg truncate">
                Denteex Dental Clinic
              </h1>
              <p className="text-emerald-100 text-xs hidden sm:block">
                Professional Dental Care
              </p>
            </div>
            <img
              src={logo}
              alt="Logo"
              className="h-6 w-6 sm:h-8 sm:w-8 rounded-full flex-shrink-0"
            />
          </div>

          <div className="mt-5 text-xs text-gray-500 text-center sm:text-right">
            Last updated: {dataRefreshTime.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6 space-y-6">
        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Total Appointments */}
          <div className="bg-blue-100 p-5 rounded-lg shadow-md flex flex-col items-center sm:col-span-2 md:col-span-1">
            <FaClipboardList className="text-blue-600 text-4xl mb-2" />
            <h3 className="text-lg font-medium text-blue-700">
              Total Appointments
            </h3>
            <p className="text-3xl font-bold">{appointments.length}</p>
          </div>

          {/* Completed and Today's Appointments - Single Row on Mobile */}
          <div className="grid grid-cols-2 gap-4 w-full sm:col-span-2">
            {/* Completed Appointments */}
            <div className="bg-green-100 p-4 rounded-lg shadow-md flex flex-col items-center">
              <FaCheckCircle className="text-green-600 text-3xl mb-1" />
              <h3 className="text-base font-medium text-green-700">
                Completed
              </h3>
              <p className="text-2xl font-bold">{completedAppointmentsCount}</p>
            </div>

            {/* Today's Appointments */}
            <div className="bg-yellow-100 p-4 rounded-lg shadow-md flex flex-col items-center">
              <FaCalendarCheck className="text-yellow-600 text-3xl mb-1" />
              <h3 className="text-base font-medium text-yellow-700">
                For Today
              </h3>
              <p className="text-2xl font-bold">{todayAppointments.length}</p>
            </div>
          </div>

          {/* Upcoming and Pending Appointments - Single Row on Mobile */}
          <div className="grid grid-cols-2 gap-4 w-full sm:col-span-2">
            {/* Upcoming Appointments */}
            <div className="bg-purple-100 p-4 rounded-lg shadow-md flex flex-col items-center">
              <FaClock className="text-purple-600 text-3xl mb-1" />
              <h3 className="text-base font-medium text-purple-700">
                Upcoming
              </h3>
              <p className="text-2xl font-bold">
                {upcomingAppointments?.length}
              </p>
            </div>

            {/* Pending Appointments */}
            <div className="bg-red-100 p-4 rounded-lg shadow-md flex flex-col items-center">
              <FaExclamationTriangle className="text-red-600 text-3xl mb-1" />
              <h3 className="text-base font-medium text-red-700">Pending</h3>
              <p className="text-2xl font-bold">{pendingAppointmentsCount}</p>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Progress Bars */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <FaChartBar className="text-blue-600 mr-3" />
              Live Status Overview
            </h2>

            <div className="space-y-6">
              {[
                {
                  label: "Total Appointments",
                  value: totalAppointments,
                  color: "#10B981",
                  max: Math.max(totalAppointments, 100),
                },
                {
                  label: "Completed",
                  value: totalCompletedAppointments,
                  color: "#3B82F6",
                  max: totalAppointments || 1,
                },
                {
                  label: "Today's",
                  value: todaysTotalAppointments,
                  color: "#8B5CF6",
                  max: Math.max(todaysTotalAppointments, 10),
                },
                {
                  label: "Upcoming",
                  value: upcomingTotalAppointments,
                  color: "#F59E0B",
                  max: Math.max(upcomingTotalAppointments, 20),
                },
                {
                  label: "Pending",
                  value: totalPendingAppointments,
                  color: "#EF4444",
                  max: Math.max(totalPendingAppointments, 10),
                },
              ].map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">
                      {item.label}
                    </span>
                    <span className="font-bold text-gray-900">
                      {item.value}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(
                          (item.value / item.max) * 100,
                          100
                        )}%`,
                        backgroundColor: item.color,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <FaChartPie className="text-purple-600 mr-3" />
              Distribution
            </h2>

            <div className="flex justify-center mb-4">
              <div className="w-48 h-48">
                <Doughnut
                  data={pieData}
                  options={{
                    plugins: {
                      legend: { display: false },
                    },
                    maintainAspectRatio: false,
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              {pieData.labels.map((label, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{
                        backgroundColor:
                          pieData.datasets[0].backgroundColor[index],
                      }}
                    ></div>
                    <span className="text-gray-600">{label}</span>
                  </div>
                  <span className="font-medium">
                    {pieData.datasets[0].data[index]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Appointments Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Today's Appointments
              </h2>
              <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                {todaysTotalAppointments}
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              <LoadingErrorComponent
                loading={appointmentsLoading}
                error={appointmentsError}
              >
                {todayAppointments.length === 0 ? (
                  <div className="text-center py-12">
                    <FaCalendarCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No appointments for today</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {todayAppointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="p-4 border border-gray-100 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {appointment.name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {appointment.reason}
                            </p>
                            <div className="flex items-center mt-2">
                              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                {appointment.timeBlock}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </LoadingErrorComponent>
            </div>
          </div>

          {/* Upcoming Appointments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
                Upcoming Appointments
              </h2>
              <span className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                {upcomingTotalAppointments}
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              <LoadingErrorComponent
                loading={appointmentsLoading}
                error={appointmentsError}
              >
                {upcomingAppointments.length === 0 ? (
                  <div className="text-center py-12">
                    <FaClock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No upcoming appointments</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="p-4 border border-gray-100 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {appointment.name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {appointment.reason}
                            </p>
                            <div className="flex items-center mt-2 space-x-2">
                              <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs">
                                {formatDate(appointment.date)}
                              </span>
                              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                {appointment.timeBlock?.split(" - ")[0]}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </LoadingErrorComponent>
            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar Widget */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
              <h2 className="text-xl font-bold text-white flex items-center">
                <FaCalendarAlt className="mr-3" />
                Calendar
              </h2>
            </div>
            <div className="p-4">
              <div style={{ height: "400px", width: "100%" }}>
                <CalendarWidget sendDataToParent={handleDataFromChild} />
              </div>
            </div>
          </div>

          {/* Selected Date Appointments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                {childDate ? formatDate(childDate) : "Selected Date"}
              </h2>
              <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                {selectedDateAppointments.length}
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              <LoadingErrorComponent
                loading={appointmentsLoading}
                error={appointmentsError}
              >
                {selectedDateAppointments.length === 0 ? (
                  <div className="text-center py-12">
                    <FaCalendarAlt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      No appointments for this date
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedDateAppointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="p-4 border border-gray-100 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {appointment.name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {appointment.reason}
                            </p>
                            <div className="flex items-center mt-2">
                              <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                                {appointment.timeBlock}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </LoadingErrorComponent>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default ReceptionistDashboard;
